// EFT (Emotional Freedom Techniques) tapping data.
// Educational self-help only — not therapy, medical treatment, or crisis support.

export type TappingPointId =
  | "karate-chop"
  | "eyebrow"
  | "side-of-eye"
  | "under-eye"
  | "under-nose"
  | "chin"
  | "collarbone"
  | "under-arm"
  | "top-of-head";

export type TappingPoint = {
  id: TappingPointId;
  label: string;
  // Coordinates on a 200x260 viewBox figure (head + torso) for the visual diagram.
  cx: number;
  cy: number;
};

export const TAPPING_POINTS: TappingPoint[] = [
  { id: "karate-chop",   label: "Karate Chop Point", cx: 168, cy: 175 },
  { id: "eyebrow",       label: "Eyebrow",           cx:  88, cy:  58 },
  { id: "side-of-eye",   label: "Side of Eye",       cx:  76, cy:  66 },
  { id: "under-eye",     label: "Under Eye",         cx:  88, cy:  78 },
  { id: "under-nose",    label: "Under Nose",        cx: 100, cy:  92 },
  { id: "chin",          label: "Chin",              cx: 100, cy: 110 },
  { id: "collarbone",    label: "Collarbone",        cx:  88, cy: 138 },
  { id: "under-arm",     label: "Under Arm",         cx:  60, cy: 168 },
  { id: "top-of-head",   label: "Top of Head",       cx: 100, cy:  18 },
];

// A "round" walks the user through the 9 points in this order.
export const ROUND_ORDER: TappingPointId[] = [
  "karate-chop",
  "eyebrow",
  "side-of-eye",
  "under-eye",
  "under-nose",
  "chin",
  "collarbone",
  "under-arm",
  "top-of-head",
];

export type TappingSession = {
  slug: string;
  title: string;
  emoji: string;
  blurb: string;
  duration: string;          // human label
  rounds: number;            // how many full rounds
  secondsPerPoint: number;   // pacing per point
  bestFor: string[];
  // Setup statement (spoken 3x on the karate chop) + a reminder phrase per remaining point.
  setup: string;
  reminders: Partial<Record<TappingPointId, string>>;
  // Optional closing phrase after the final round.
  closing?: string;
};

// One universal EFT tapping session — suitable for everyday emotional regulation.
export const UNIVERSAL_TAPPING_SESSION: TappingSession = {
  slug: "universal",
  title: "EFT Tapping",
  emoji: "💛",
  blurb:
    "One simple, guided tapping sequence you can use whenever you feel overwhelmed, anxious, stressed, emotionally activated, or need a moment to reconnect with yourself.",
  duration: "6 min",
  rounds: 2,
  secondsPerPoint: 10,
  bestFor: [
    "Anxiety",
    "Stress",
    "Emotional Overwhelm",
    "Racing Thoughts",
    "Grounding",
    "Trauma Recovery",
    "Flashbacks",
    "Self-Compassion",
    "Nervous System Regulation",
  ],
  setup:
    "Even though I'm feeling this right now, I deeply and completely accept myself, exactly as I am.",
  reminders: {
    eyebrow: "This feeling I'm noticing…",
    "side-of-eye": "All of what's here in my body…",
    "under-eye": "It's safe to feel this…",
    "under-nose": "I don't have to fix it all right now…",
    chin: "I'm allowed to slow down…",
    collarbone: "I'm letting my body know it's safe…",
    "under-arm": "I'm here with myself now…",
    "top-of-head": "Softening, one breath at a time.",
  },
  closing:
    "Take a slow breath in. A longer breath out. Notice what has shifted, however small.",
};

// Kept for compatibility with call sites that suggested a tapping practice from
// journal text — now always returns the single universal session when keywords
// suggest tapping could help.
export function suggestTappingForText(text: string): TappingSession | null {
  const t = text.toLowerCase();
  if (
    /\b(anxious|anxiety|worry|worried|nervous|on edge|overwhelm|overwhelmed|too much|stress|stressed|flashback|trigger|triggered|panic|dissociat|numb|shame|worthless|anger|angry|furious|rage|grief|loss|fear|afraid|scared|racing thoughts)\b/.test(
      t,
    )
  ) {
    return UNIVERSAL_TAPPING_SESSION;
  }
  return null;
}
